import React from 'react';
import { examSetupDisplay } from '../../../../constants/blueboxExamStatus';
import { formatStudentName } from '../../../../utils/common';
import { generateStatus } from '../../../common/students/utility';
import { getGroupTypes } from '../../../../utils/getGroupTypes';
import { hasAdminAccess } from '../../../../utils/user';
import { isInSchoolAPEvents } from '../../../../utils/event';
import { Link, useLocation } from 'react-router-dom';
import { ResizeContext } from '../../../../context/ResizeContext';
import { useStateValue } from '../../../../context/AppContext';
import moment from 'moment';
import { generateGroupTypeCellStr } from '../../../../utils/groupTypeLabels';
import IconStatus from '../../../ui/icons/IconStatus';
import { formattedDate } from '../../../../utils/date';
import {
  generateDistrictId,
  generateSchoolId,
  generateStateId,
  FALLBACK_STUDENT_ID_ROSTER,
} from '../../../../utils/getStudentId';
import { IGetStudent, IGetStudentPaginatedRosterData } from '../../../../apollo/types';
import AccommodationList from '../../../common/accommodations/AccommodationList';

interface StudentRosterRowProps {
  columns: {
    title: string;
    fieldName: string;
    visible?: boolean;
    adminOnly?: boolean;
    headerDef?: { title: string; sortField: string[] };
  }[];
  handleCheckStudents: (students: string[], checkAll?: boolean) => void;
  loading?: boolean;
  // eslint-disable-next-line
  options: Record<string, any>;
  student: IGetStudent | IGetStudentPaginatedRosterData;
  renderCheckBoxes?: boolean;
}

function RenderStartTime({ examStartTime }: { examStartTime: string | null }) {
  if (!examStartTime) return <td>Not started</td>;
  const m = moment(examStartTime).local();
  return (
    <td style={{ whiteSpace: 'nowrap' }}>
      <div>{m.format('LT')}</div>
      <div>{m.format('MMMM D, YYYY')}</div>
    </td>
  );
}

function RenderEstEndTime({ estimatedExamEndTime }: { estimatedExamEndTime: string | null }) {
  if (!estimatedExamEndTime) return <td>Not started</td>;
  const m = moment(estimatedExamEndTime).local();
  return (
    <td style={{ whiteSpace: 'nowrap' }}>
      <div>{m.format('LT')}</div>
      <div>{m.format('MMMM D, YYYY')}</div>
    </td>
  );
}

function StudentRosterRow({
  columns = [],
  handleCheckStudents = () => {},
  loading = false,
  options = {},
  student: studentProp,
  renderCheckBoxes = true,
}: StudentRosterRowProps) {
  const student = studentProp as IGetStudent;
  const normalizedStudentIds = Array.isArray(studentProp.studentIds)
    ? studentProp.studentIds
    : studentProp.studentIds
      ? [studentProp.studentIds]
      : [];

  // Global App state.
  const { orgEvent, user } = useStateValue();
  const windowSize = React.useContext(ResizeContext);

  // History for current page so we can return to it later.
  const { pathname } = useLocation();

  const room = student.room || {};
  const { hiddenFields = {}, links = true } = options;
  const groupTypes = getGroupTypes(orgEvent);
  const group = groupTypes[student.groupType];

  const statusColor = generateStatusColor(student);
  const checkId = `bulk-check-${student.id}`;
  const checkName = `bulk-check[${student.id}]`;
  const linkId = `student-details-${student.id}`;
  const groupTypeInfoId = `groupType-Info-${student.id}`;

  function renderCheckAll() {
    return (
      hasAdminAccess(user.rid, user.role) && (
        <td>
          <label htmlFor={checkId}>
            <input
              checked={options.checked ? true : false}
              disabled={loading}
              id={checkId}
              name={checkName}
              onChange={checkStudent(student.id)}
              type='checkbox'
              value={student.id}
            />
            <span className='cb-sr-only'>Select {formatStudentName(student)} for a bulk operation</span>
          </label>
        </td>
      )
    );
  }

  function checkStudent(studentId: string) {
    return function () {
      handleCheckStudents([studentId]);
    };
  }

  function generateStatusColor(student: IGetStudent) {
    // Determine which color to use for Student Status.
    if (student.checkedInRoom || student.checkedInCenter) {
      return 'green3';
    } else if (student.absent || student.deniedEntry) {
      return 'red1';
    }
  }

  function defaultMobileComponent() {
    return (
      <li data-automation={'student-roster-row'}>
        <Link id={linkId} to={'/students/get/' + student.id} state={{ from: pathname }}>
          <div className='tdtk-item-text'>
            <div className='tdtk-item-title cb-roboto-bold'>
              <div className='row mx-0 px-0'>
                <div className='col-xs-7'>
                  <div className='row' style={{ textDecoration: 'none' }}>
                    {formatStudentName(student)}
                  </div>
                  <div className='row text--color-black text--weight-normal'>
                    {!isInSchoolAPEvents(orgEvent) ? (
                      <div className='student-table-row-info--reg'>
                        {student?.displayedRegNo && <span> Reg. no.: {student?.displayedRegNo}</span>}
                      </div>
                    ) : (
                      ''
                    )}
                    {student?.waitListFlag === 'Y' ? <div className='student-table-row-info--reg'>Waitlisted</div> : ''}
                  </div>
                </div>
                <div className='col-xs-5'>
                  <div className='display-flex cb-float-left'>
                    <IconStatus
                      color={statusColor}
                      text={generateStatus(student).statusText}
                      textClass='cb-gray1-color'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </li>
    );
  }

  function testDayLiveMobileComponent() {
    return (
      <li data-automation={'student-roster-row'}>
        <div className='tdtk-item-text'>
          <div className='tdtk-item-title cb-roboto-bold'>
            <div className='row mx-0 px-0'>
              <div className='col-xs-7'>
                <div className='row text--color-black'>{formatStudentName(student)}</div>
                <div className='row text--color-black text--weight-normal'>
                  {!isInSchoolAPEvents(orgEvent) ? (
                    <div className='student-table-row-info--reg'>
                      {student?.displayedRegNo && <span> Reg. no.: {student?.displayedRegNo}</span>}
                    </div>
                  ) : (
                    ''
                  )}
                  {student?.waitListFlag === 'Y' ? <div className='student-table-row-info--reg'>Waitlisted</div> : ''}
                </div>
              </div>
              <div className='col-xs-5'>
                <div className='display-flex cb-float-left text--color-black text--weight-normal'>
                  {room.title ? room.title : 'Unassigned'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  }

  function mobileComponent(pathname: string) {
    // (ref: livedata_url_path)
    if (pathname === '/livedata') {
      return testDayLiveMobileComponent();
    }
    return defaultMobileComponent();
  }

  function renderStudentName() {
    return (
      <th scope='row'>
        <strong>
          {links ? (
            <Link id={linkId} to={'/students/get/' + student.id} state={{ from: pathname }}>
              {formatStudentName(student)}
            </Link>
          ) : (
            <span>{formatStudentName(student)}</span>
          )}
        </strong>
        {!isInSchoolAPEvents(orgEvent) ? (
          <div className='student-table-row-info--reg'>
            {student?.displayedRegNo && <span> Reg. no.: {student?.displayedRegNo}</span>}
          </div>
        ) : (
          ''
        )}
        {student.waitListFlag === 'Y' && (
          <>
            <br />
            <strong>Waitlisted</strong>
          </>
        )}
      </th>
    );
  }

  function renderTestingGroup() {
    return (
      <td>
        <div id={groupTypeInfoId}>{generateGroupTypeCellStr(group, student)}</div>
      </td>
    );
  }

  function renderAccommodations() {
    return (
      <td>
        {student?.accommodationsArray?.length > 0 ? (
          <ul
            className='cb-resource'
            style={{
              listStyle: 'disc',
              padding: '0 1rem 0 1rem',
            }}
          >
            <AccommodationList accommodations={student?.accommodationsArray} studentId={student?.id} links={links} />
          </ul>
        ) : (
          'None'
        )}
      </td>
    );
  }

  function renderRoom() {
    return (
      <td>{room.title ? links ? <Link to={`/rooms/get/${room.id}`}>{room.title}</Link> : room.title : 'Unassigned'}</td>
    );
  }

  function renderAttendance() {
    return (
      <td>
        <IconStatus
          showIcon={generateStatus(student).statusText !== 'Not arrived'}
          color={statusColor}
          text={generateStatus(student).statusText}
        />
      </td>
    );
  }

  function renderExamSetUpStatus() {
    return <td aria-label={`Exam status for student id - ${student?.id}`}>{examSetupDisplay(student)}</td>;
  }

  function renderTestingStatus() {
    return <td>{student?.dapTestStatus?.displayName}</td>;
  }

  function renderLastUpdated() {
    return (
      <td>
        {moment(student.updated || undefined)
          .local()
          .format('lll')}
      </td>
    );
  }

  function renderSpecialUseOnly() {
    return <td className='cb-resource'>{student?.specialUseOnly?.join(', ') ?? 'N/A'}</td>;
  }

  function renderTestName() {
    return <td className='cb-resource'>{student?.testDisplayName ?? 'N/A'}</td>;
  }

  function renderGradeLevel() {
    return <td className='cb-resource'>{student?.gradeLevel ?? 'N/A'}</td>;
  }

  function renderCandDOB() {
    return <td className='cb-resource'>{student?.candDOB ? formattedDate(student.candDOB) : 'N/A'}</td>;
  }

  function renderStudentStateID() {
    return (
      <td className='cb-resource'>
        <span id={`student-stateId-${student.candRegNo}`}>
          {generateStateId(normalizedStudentIds, FALLBACK_STUDENT_ID_ROSTER)}
        </span>
      </td>
    );
  }

  function renderStudentDistrictID() {
    return (
      <td className='cb-resource'>
        <span id={`student-districtId-${student.candRegNo}`}>
          {generateDistrictId(normalizedStudentIds, FALLBACK_STUDENT_ID_ROSTER)}
        </span>
      </td>
    );
  }

  function renderStudentSchoolID() {
    return (
      <td className='cb-resource'>
        <span id={`student-schoolId-${student.candRegNo}`}>
          {generateSchoolId(normalizedStudentIds, FALLBACK_STUDENT_ID_ROSTER)}
        </span>
      </td>
    );
  }

  const components: Record<string, { renderer: () => JSX.Element }> = {
    testingGroup: {
      renderer: renderTestingGroup,
    },
    accommodations: { renderer: renderAccommodations },
    room: {
      renderer: renderRoom,
    },
    attendance: {
      renderer: renderAttendance,
    },
    testingStatus: {
      renderer: renderTestingStatus,
    },
    examStatus: {
      renderer: renderExamSetUpStatus,
    },
    specialUseOnly: {
      renderer: renderSpecialUseOnly,
    },
    testDisplayName: {
      renderer: renderTestName,
    },
    gradeLevel: {
      renderer: renderGradeLevel,
    },
    candDOB: {
      renderer: renderCandDOB,
    },
    studentStateID: {
      renderer: renderStudentStateID,
    },
    studentDistrictID: {
      renderer: renderStudentDistrictID,
    },
    studentSchoolID: {
      renderer: renderStudentSchoolID,
    },
    lastUpdated: {
      renderer: renderLastUpdated,
    },
    startTime: {
      renderer: () => <RenderStartTime examStartTime={student.examStartTime} />,
    },
    estEndTime: {
      renderer: () => <RenderEstEndTime estimatedExamEndTime={student.estimatedExamEndTime} />,
    },
  };

  function desktopComponent() {
    return (
      <tr>
        {renderCheckBoxes && renderCheckAll()}
        {renderStudentName()}
        {columns.map(
          ({ fieldName }) =>
            !hiddenFields?.[fieldName] && (
              <React.Fragment key={fieldName}>{components[fieldName]?.renderer()}</React.Fragment>
            )
        )}
      </tr>
    );
  }

  return windowSize.mobile ? mobileComponent(pathname) : desktopComponent();
}

export default StudentRosterRow;
